import * as React from 'react';

import useUrlState from '@ahooksjs/use-url-state';
import { useGetState, useMemoizedFn, useRequest } from 'ahooks';
import { Helmet } from 'react-helmet-async';

import { searchProducts } from 'services';
import type { ProductStatus } from 'types';
import { PATHNAME } from 'utils';

import { ProductsHeader, ProductsTable } from './components';

const {
  QUERY_PARAM: {
    DEFAULT: {
      SORT_TYPE: DEFAULT_SORT_TYPE,
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      PAGE_SIZE: DEFAULT_PAGE_SIZE,
    },
    SORT_TYPE: { BASE_URL: SORT_TYPE_BASE_URL },
    SEARCH_KEY: { BASE_URL: SEARCH_KEY_BASE_URL },
    PAGE_NUMBER: { BASE_URL: PAGE_NUMBER_BASE_URL },
  },
} = PATHNAME;

export default function Products() {
  const [
    {
      [SORT_TYPE_BASE_URL]: sortType,
      [SEARCH_KEY_BASE_URL]: searchKey,
      [PAGE_NUMBER_BASE_URL]: pageNumber,
    },
    setUrlState,
  ] = useUrlState(
    {
      [SORT_TYPE_BASE_URL]: DEFAULT_SORT_TYPE,
      [SEARCH_KEY_BASE_URL]: '',
      [PAGE_NUMBER_BASE_URL]: DEFAULT_PAGE_NUMBER,
    },
    { navigateMode: 'replace' },
  );
  const [pageSize, setPageSize] = useGetState<number>(DEFAULT_PAGE_SIZE);
  const [categoriesIds, setCategoriesIds] = useGetState<string[]>();
  const [brandsIds, setBrandsIds] = useGetState<string[]>();
  const [statuses, setStatuses] = useGetState<ProductStatus[]>();
  const [callForPrice, setCallForPrice] = useGetState<boolean>();
  const [isIndex, setIndex] = useGetState<boolean>();
  const [lowerBoundCreateDate, setLowerBoundCreateDate] = useGetState<string>();
  const [upperBoundCreateDate, setUpperBoundCreateDate] = useGetState<string>();
  const { data, loading, refresh } = useRequest(
    async () =>
      await searchProducts({
        sortType,
        searchKey,
        pageNumber,
        pageSize,
        categoriesIds,
        brandsIds,
        statuses,
        callForPrice,
        isIndex,
        lowerBoundCreateDate,
        upperBoundCreateDate,
      }),
    {
      refreshDeps: [
        sortType,
        searchKey,
        pageNumber,
        pageSize,
        categoriesIds,
        brandsIds,
        statuses,
        callForPrice,
        isIndex,
        lowerBoundCreateDate,
        upperBoundCreateDate,
      ],
    },
  );

  const { total = 0, data: products = [] } = data ?? {};

  const handleSearchKey = useMemoizedFn((updatedSearchKey: string) => {
    setUrlState({ [SEARCH_KEY_BASE_URL]: updatedSearchKey });
  });
  const handlePageNumber = useMemoizedFn((updatedPageNumber: number) => {
    setUrlState({ [PAGE_NUMBER_BASE_URL]: updatedPageNumber });
  });
  const handlePageSize = useMemoizedFn((updatedPageSize: number) => {
    setPageSize(updatedPageSize);
  });
  const handleCategoriesIds = useMemoizedFn(
    (updatedCategoriesIds: string[]) => {
      setCategoriesIds(updatedCategoriesIds);
    },
  );
  const handleBrandsIds = useMemoizedFn((updatedBrandsIds: string[]) => {
    setBrandsIds(updatedBrandsIds);
  });
  const handleStatuses = useMemoizedFn((updatedStatuses: ProductStatus[]) => {
    setStatuses(updatedStatuses);
  });
  const handleCallForPrice = useMemoizedFn((updatedCallForPrice: boolean) => {
    setCallForPrice(updatedCallForPrice);
  });
  const handleIndex = useMemoizedFn((updatedIsIndex: boolean) => {
    setIndex(updatedIsIndex);
  });
  const handleLowerBoundCreateDate = useMemoizedFn(
    (updatedLowerBoundCreateDate?: string) => {
      setLowerBoundCreateDate(updatedLowerBoundCreateDate);
    },
  );
  const handleUpperBoundCreateDate = useMemoizedFn(
    (updatedUpperBoundCreateDate?: string) => {
      setUpperBoundCreateDate(updatedUpperBoundCreateDate);
    },
  );

  return (
    <React.Fragment>
      <Helmet>
        <title>محصول‌ها | کاشی لند</title>
      </Helmet>
      <div className='rounded bg-white p-6'>
        <ProductsHeader
          searchKey={searchKey}
          handleSearchKey={handleSearchKey}
          handleLowerBoundCreateDate={handleLowerBoundCreateDate}
          handleUpperBoundCreateDate={handleUpperBoundCreateDate}
        />
        <ProductsTable
          loading={loading}
          total={total}
          products={products}
          pageSize={pageSize}
          pageNumber={pageNumber}
          handleCategoriesIds={handleCategoriesIds}
          handleBrandsIds={handleBrandsIds}
          handleStatuses={handleStatuses}
          handleCallForPrice={handleCallForPrice}
          handleIndex={handleIndex}
          handlePageSize={handlePageSize}
          handlePageNumber={handlePageNumber}
          handleRefreshData={refresh}
        />
      </div>
    </React.Fragment>
  );
}
